import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import config from '../../config';
import { useAdminApi } from '../hooks/useAdminApi';

interface AdminAiAssistantModalProps {
  open: boolean;
  title: string;
  promptLabel?: string;
  promptPlaceholder?: string;
  defaultPrompt?: string;
  contextLabel?: string;
  contextPlaceholder?: string;
  initialContext?: string;
  applyLabel?: string;
  promptKey?: string;
  courseOptions?: Array<{ value: string; label: string }>;
  selectedCourseValue?: string;
  onCourseSelectionChange?: (value: string) => void;
  courseSelectorLabel?: string;
  courseSelectorPlaceholder?: string;
  requireCourseSelection?: boolean;
  extraControls?: React.ReactNode;
  onClose: () => void;
  onApply: (generatedText: string, metadata?: { promptKey?: string; promptVersion?: string; model?: string }) => void;
}

const AdminAiAssistantModal: React.FC<AdminAiAssistantModalProps> = ({
  open,
  title,
  promptLabel = 'What would you like the assistant to generate?',
  promptPlaceholder = 'Describe the content you want the assistant to draft or improve.',
  defaultPrompt = '',
  contextLabel = 'Current context',
  contextPlaceholder = 'Add the page context, existing text, or field values to guide the response.',
  initialContext = '',
  applyLabel = 'Apply to current field',
  promptKey = 'admin-ai-assistant',
  courseOptions,
  selectedCourseValue = '',
  onCourseSelectionChange,
  courseSelectorLabel = 'Course',
  courseSelectorPlaceholder = 'Select a course',
  requireCourseSelection = false,
  extraControls,
  onClose,
  onApply,
}) => {
  const { hasActiveOrganization, getManagementToken, getOrganizationHeaders } = useAdminApi();
  const [promptText, setPromptText] = useState(defaultPrompt);
  const [contextText, setContextText] = useState(initialContext);
  const [responseText, setResponseText] = useState('');
  const [responseMetadata, setResponseMetadata] = useState<{ promptKey?: string; promptVersion?: string; model?: string }>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    setPromptText(defaultPrompt);
    setContextText(initialContext);
    setResponseText('');
    setResponseMetadata({});
    setError(null);
    setIsGenerating(false);
  }, [defaultPrompt, initialContext, open]);

  const apiBaseUrl = useMemo(() => `${config.apiBaseUrl}/api`, []);

  const handleGenerate = async () => {
    if (!promptText.trim()) {
      setError('Please enter a prompt for the assistant.');
      return;
    }

    if (!hasActiveOrganization) {
      setError('Please select an organization before using AI Assistant.');
      return;
    }

    if (requireCourseSelection && !selectedCourseValue.trim()) {
      setError('Please select a course before generating bulk category drafts.');
      return;
    }

    try {
      setIsGenerating(true);
      setError(null);
      const token = await getManagementToken();
      const response = await axios.post(
        `${apiBaseUrl}/admin-ai-assistant/generate`,
        {
          promptKey,
          promptText: promptText.trim(),
          contextText: contextText.trim() || undefined,
          requestedAction: title,
          selectedCourseName: selectedCourseValue.trim() || undefined,
        },
        {
          headers: getOrganizationHeaders(token),
        }
      );

      const generatedText = response.data?.generatedText || '';
      setResponseMetadata({
        promptKey: response.data?.promptKey,
        promptVersion: response.data?.promptVersion,
        model: response.data?.model,
      });
      setResponseText(generatedText);
    } catch (err: any) {
      console.error('AI Assistant generation failed', err);
      setError(err.response?.data?.message || err.message || 'AI generation failed.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleApply = () => {
    if (!responseText.trim()) {
      return;
    }

    onApply(responseText.trim(), responseMetadata.promptKey || promptKey ? {
      promptKey: responseMetadata.promptKey || promptKey,
      promptVersion: responseMetadata.promptVersion,
      model: responseMetadata.model,
    } : undefined);
    onClose();
  };

  if (!open) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(12, 24, 42, 0.68)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 3000,
      }}
      role="presentation"
      onClick={onClose}
    >
      <div
        style={{
          width: 'min(860px, 100%)',
          maxHeight: '90vh',
          overflowY: 'auto',
          borderRadius: 16,
          background: '#fff',
          boxShadow: '0 20px 40px rgba(15, 23, 42, 0.2)',
          padding: 20,
          display: 'grid',
          gap: 12,
          zIndex: 3001,
        }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, color: '#142541' }}>{title}</h3>
            <div style={{ fontSize: '0.82rem', color: '#5f6f89', marginTop: 4 }}>
              Draft content, improve existing copy, or generate a structured response.
            </div>
          </div>
          <button type="button" className="amd-btn amd-btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>

        <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', fontWeight: 700, color: '#243b69' }}>
          {promptLabel}
          <textarea
            value={promptText}
            onChange={(event) => setPromptText(event.target.value)}
            rows={4}
            placeholder={promptPlaceholder}
            style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', resize: 'vertical' }}
          />
        </label>

        <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', fontWeight: 700, color: '#243b69' }}>
          {contextLabel}
          <textarea
            value={contextText}
            onChange={(event) => setContextText(event.target.value)}
            rows={4}
            placeholder={contextPlaceholder}
            style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', resize: 'vertical' }}
          />
        </label>

        {courseOptions && courseOptions.length > 0 && (
          <label style={{ display: 'grid', gap: 6, fontSize: '0.84rem', fontWeight: 700, color: '#243b69' }}>
            {courseSelectorLabel}
            <select
              value={selectedCourseValue}
              onChange={(event) => onCourseSelectionChange?.(event.target.value)}
              style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', background: '#fff' }}
            >
              <option value="">{courseSelectorPlaceholder}</option>
              {courseOptions.map((course) => (
                <option key={course.value} value={course.value}>{course.label}</option>
              ))}
            </select>
          </label>
        )}

        {extraControls}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="amd-btn amd-btn-primary" onClick={() => void handleGenerate()} disabled={isGenerating}>
            {isGenerating ? 'Generating…' : 'Generate'}
          </button>
          <button type="button" className="amd-btn amd-btn-secondary" onClick={() => { setPromptText(defaultPrompt); setContextText(initialContext); setResponseText(''); setError(null); }}>
            Reset
          </button>
        </div>

        {error && (
          <div style={{ borderRadius: 10, padding: '10px 12px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', fontSize: '0.84rem' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'grid', gap: 6 }}>
          <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#243b69' }}>Response</div>
          <textarea
            value={responseText}
            onChange={(event) => setResponseText(event.target.value)}
            rows={10}
            placeholder="The assistant response will appear here."
            style={{ border: '1px solid #c9d6e8', borderRadius: 10, padding: '9px 10px', fontSize: '0.9rem', resize: 'vertical', minHeight: 180 }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="amd-btn amd-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="amd-btn amd-btn-primary" onClick={handleApply} disabled={!responseText.trim()}>
            {applyLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminAiAssistantModal;
